import { KeyboardEvent, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import {
    contextMenuPoint,
    type ContextMenuOpenedBy,
    type ContextMenuTriggerEvent,
    type IContextMenuPoint,
    isContextMenuKey,
    isKeyboardContextMenu,
} from './contextMenuTrigger';

/** Сколько после открытия с клавиатуры не открывать меню на клавиатурный `contextmenu` (Windows шлёт его вслед), мс */
const KEYBOARD_ECHO_MS = 600;

export interface IContextMenuState<T> {
    point: IContextMenuPoint;
    /** Что под меню: строка, узел — из чего строить пункты */
    data: T;
    openedBy: ContextMenuOpenedBy;
    /** Куда вернуть фокус после закрытия: элемент, где он был при открытии */
    returnFocus: HTMLElement | null;
}

export interface IContextMenuOpenOptions {
    openedBy?: ContextMenuOpenedBy;
    /** По умолчанию — элемент в фокусе */
    returnFocus?: HTMLElement | null;
}

export interface IContextMenuTargetProps {
    onContextMenu: (event: MouseEvent<HTMLElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

const focusedElement = () => {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body ? active : null;
};

/**
 * Состояние контекстного меню: открыть в точке события (`openAt` — щелчок правой кнопкой, Shift+F10, клавиша меню),
 * закрыть, пропсы для `<ContextMenu {...menuProps}>`. Обработчики цели — `targetProps(data)`: `onContextMenu` и
 * `onKeyDown` (Shift+F10 и клавиша меню на элементе в фокусе — меню у него, фокус на первом пункте).
 */
export const useContextMenu = <T = undefined>() => {
    const [state, setState] = useState<IContextMenuState<T> | null>(null);
    // после закрытия меню уходит из прежней точки
    const [lastPoint, setLastPoint] = useState<IContextMenuPoint | null>(null);
    const echoUntil = useRef(-Infinity);

    const open = useCallback((point: IContextMenuPoint, data: T, options: IContextMenuOpenOptions = {}) => {
        setLastPoint(point);
        setState({
            point,
            data,
            openedBy: options.openedBy ?? 'pointer',
            returnFocus: options.returnFocus === undefined ? focusedElement() : options.returnFocus,
        });
    }, []);

    const openAt = useCallback(
        (event: ContextMenuTriggerEvent & { preventDefault: () => void }, data: T) => {
            event.preventDefault();
            const keyboard = isKeyboardContextMenu(event);
            // эхо клавиатурного открытия — тоже «с клавиатуры»; щелчок правой кнопкой открывает меню и сразу после
            if (event.type === 'contextmenu' && keyboard && performance.now() < echoUntil.current) return;
            if (event.type === 'keydown') echoUntil.current = performance.now() + KEYBOARD_ECHO_MS;
            open(contextMenuPoint(event), data, { openedBy: keyboard ? 'keyboard' : 'pointer' });
        },
        [open],
    );

    const close = useCallback(() => setState(null), []);

    const targetProps = useCallback(
        (data: T): IContextMenuTargetProps => ({
            onContextMenu: (event) => openAt(event, data),
            onKeyDown: (event) => {
                if (isContextMenuKey(event)) openAt(event, data);
            },
        }),
        [openAt],
    );

    const menuProps = useMemo(
        () => ({
            opened: state !== null,
            point: state?.point ?? lastPoint,
            onClose: close,
            openedBy: state?.openedBy,
            returnFocus: state?.returnFocus ?? null,
        }),
        [state, lastPoint, close],
    );

    return { state, open, openAt, close, menuProps, targetProps };
};
